const normalizeHex = (hex) => {
	const trimmed = hex?.trim().toLowerCase() ?? '';
	if (/^#[0-9a-f]{3}$/.test(trimmed)) {
		const [, r, g, b] = trimmed;
		return `#${r}${r}${g}${g}${b}${b}`;
	}
	return trimmed;
};

const getRelativeLuminance = (hex) => {
	const normalized = normalizeHex(hex);
	if (!/^#[0-9a-f]{6}$/.test(normalized)) {
		return null;
	}
	const channels = [1, 3, 5].map((index) =>
		parseInt(normalized.slice(index, index + 2), 16) / 255,
	);
	const linear = channels.map((channel) =>
		channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
	);
	return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
};

const getContrastRatio = (foreground, background) => {
	const fg = getRelativeLuminance(foreground);
	const bg = getRelativeLuminance(background);
	if (fg == null || bg == null) {
		return null;
	}
	const lighter = Math.max(fg, bg);
	const darker = Math.min(fg, bg);
	return (lighter + 0.05) / (darker + 0.05);
};

/** Finvu default brand — never use entity primary as fallback (may be white). */
const BRAND_CTA_BG = '#3053D3';
const BRAND_CTA_TEXT = '#FFFFFF';

/** WCAG AA guard for entity-injected CTA colors (e.g. white on white). */
export const getAccessibleCtaColors = (
	customDetails,
	isDark,
	{ fallbackBg = BRAND_CTA_BG, fallbackText = BRAND_CTA_TEXT } = {},
) => {
	let backgroundColor =
		customDetails?.ctaBackgroundColor ||
		customDetails?.primaryColor ||
		fallbackBg;
	let color = customDetails?.ctaTextColor || fallbackText;

	let ratio = getContrastRatio(color, backgroundColor);
	const bgLuminance = getRelativeLuminance(backgroundColor);

	if (ratio == null || ratio < 4.5) {
		const lightBackground = bgLuminance != null && bgLuminance > 0.5;
		color = lightBackground ? '#000000' : BRAND_CTA_TEXT;
		ratio = getContrastRatio(color, backgroundColor);
	}

	if (ratio == null || ratio < 4.5) {
		backgroundColor = BRAND_CTA_BG;
		color = BRAND_CTA_TEXT;
	}

	return { backgroundColor, color };
};

/** Opaque modal/drawer surface — never use entity secondary (may be translucent/tinted). */
export const getOpaqueDialogSurface = (isDark) =>
	isDark ? '#161616' : '#FFFFFF';

/** Body copy color that meets 4.5:1 on the given opaque surface. */
export const getAccessibleBodyTextColor = (backgroundColor, isDark) => {
	if (isDark) {
		return '#B0B0B0';
	}
	const candidates = ['#5F6368', '#424242', '#212121'];
	for (const candidate of candidates) {
		const ratio = getContrastRatio(candidate, backgroundColor);
		if (ratio != null && ratio >= 4.5) {
			return candidate;
		}
	}
	return '#212121';
};

const resolveAccessibleCtaHoverBg = (customDetails, isDark, backgroundColor) => {
	const hoverCandidate =
		customDetails?.ctaHoverPrimaryColor || backgroundColor;
	return getAccessibleCtaColors(
		{
			...customDetails,
			ctaBackgroundColor: hoverCandidate,
			primaryColor: hoverCandidate,
		},
		isDark,
	).backgroundColor;
};

const disabledCtaBg = (isDark) => (isDark ? '#7A8185' : '#E0E0E0');
const disabledCtaText = (isDark) => (isDark ? '#161616' : '#757575');

/** Theme-level hover bg with the same WCAG guard as default CTA state. */
export const getAccessibleCtaHoverBg = resolveAccessibleCtaHoverBg;

/** Entity theme colors — no WCAG brand-blue fallback (matches Login CTAs). */
export const getEntityThemedCtaColors = (customDetails, isDark) => {
	const backgroundColor =
		customDetails?.ctaBackgroundColor ?? customDetails?.primaryColor;
	const hoverBackgroundColor =
		customDetails?.ctaHoverPrimaryColor ?? customDetails?.primaryColor;
	const color =
		customDetails?.ctaTextColor ?? (isDark ? '#161616' : '#ffffff');
	return { backgroundColor, hoverBackgroundColor, color };
};

/** Inline style for native `<button>` / Box component="button" entity CTAs. */
export const getEntityThemedCtaInlineStyle = (
	customDetails,
	isDark,
	extraStyle = {},
) => {
	const { backgroundColor, color } = getEntityThemedCtaColors(
		customDetails,
		isDark,
	);
	return {
		backgroundColor,
		color,
		opacity: 1,
		backgroundImage: 'none',
		...extraStyle,
	};
};

/**
 * Entity white-label CTA sx — matches Login / legacy discovery (dark text on light primary in dark mode).
 * Use on screens where product explicitly configures ctaTextColor + primaryColor per theme.
 */
export const getEntityThemedCtaButtonSx = (customDetails, isDark, extraSx = {}) => {
	const {
		'&:hover': hoverExtra,
		'&.Mui-disabled': disabledExtra,
		...restExtra
	} = extraSx;
	const { backgroundColor, hoverBackgroundColor, color } =
		getEntityThemedCtaColors(customDetails, isDark);
	const ctaStyles = {
		backgroundColor: `${backgroundColor} !important`,
		color: `${color} !important`,
	};

	return {
		...restExtra,
		...ctaStyles,
		boxShadow: 'none',
		'&.MuiButton-contained, &.MuiButton-containedPrimary': ctaStyles,
		'& .MuiButton-endIcon': { color: `${color} !important` },
		'&:hover': {
			backgroundColor: `${hoverBackgroundColor} !important`,
			color: `${color} !important`,
			boxShadow: 'none',
			...hoverExtra,
		},
		'&.Mui-disabled': {
			backgroundColor: `${isDark ? '#7A8185' : '#E0E0E0'} !important`,
			color: `${isDark ? '#161616' : '#757575'} !important`,
			opacity: '1 !important',
			...disabledExtra,
		},
	};
};

/** Shared sx for entity-themed contained CTAs (use when theme overrides are insufficient). */
export const getCtaButtonSx = (customDetails, isDark, extraSx = {}) => {
	const { backgroundColor, color } = getAccessibleCtaColors(customDetails, isDark);
	const hoverBg = resolveAccessibleCtaHoverBg(customDetails, isDark, backgroundColor);
	const {
		'&.Mui-disabled': disabledExtra,
		'&:hover': hoverExtra,
		...restExtra
	} = extraSx;
	const ctaStyles = {
		backgroundColor: `${backgroundColor} !important`,
		color: `${color} !important`,
	};
	return {
		...restExtra,
		...ctaStyles,
		'&.MuiButton-contained, &.MuiButton-containedPrimary': ctaStyles,
		'&:hover': {
			backgroundColor: `${hoverBg} !important`,
			color: `${color} !important`,
			...hoverExtra,
		},
		'&.Mui-disabled': {
			backgroundColor: `${disabledCtaBg(isDark)} !important`,
			color: `${disabledCtaText(isDark)} !important`,
			opacity: '1 !important',
			...disabledExtra,
		},
	};
};

